import { SearchParamsType } from "../types";
import {
  PetAPIResponseType,
  PetListAPIResponseType,
  PetPaginatedAPIResponseType,
} from "../types/pet";
import { UserProfileAPIResponseType } from "../types/user";
import { logErrors } from "./utils";

export const apiUrl: string = process.env.API_URL;

export const getInfo = async () => {
  try {
    const response = await fetch(`${apiUrl}/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (!response.ok) {
      logErrors(`Unable to fetch data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }
    const data = (await response.json()) as { message: string };
    if (!data) {
      logErrors("No data in response");
      return;
    }
    return data;
  } catch (error) {
    logErrors(error);
  }
};

export const getUserProfile = async (accessToken: string) => {
  try {
    const response = await fetch(`${apiUrl}/user/profile`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      logErrors(`Unable to fetch data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as {
      data: UserProfileAPIResponseType;
    };

    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const updateUserProfile = async (accessToken: string, dt: FormData) => {
  try {
    const response = await fetch(`${apiUrl}/user/profile`, {
      method: "PUT",
      headers: {
        // "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${accessToken}`,
      },
      body: dt,
    });

    if (!response.ok) {
      logErrors(`Unable to update data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as {
      data: UserProfileAPIResponseType;
    };

    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const getUserPets = async (accessToken: string) => {
  try {
    const response = await fetch(`${apiUrl}/user/pets`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      logErrors(`Unable to fetch data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as PetListAPIResponseType;
    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const getUserPetByID = async (accessToken: string, id: string) => {
  try {
    const response = await fetch(`${apiUrl}/user/pets/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      logErrors(`Unable to fetch data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as { data: PetAPIResponseType };
    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const updateUserPetByID = async (accessToken: string, id: string, dt: FormData) => {
  try {
    const response = await fetch(`${apiUrl}/user/pets/${id}`, {
      method: "PUT",
      headers: {
        // "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${accessToken}`,
      },
      body: dt,
    });

    if (!response.ok) {
      logErrors(`Unable to update data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as {
      data: PetAPIResponseType;
    };

    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const createUserPet = async (accessToken: string, dt: FormData) => {
  try {
    const response = await fetch(`${apiUrl}/user/pets/`, {
      method: "POST",
      headers: {
        // "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${accessToken}`,
      },
      body: dt,
    });

    if (!response.ok) {
      logErrors(`Unable to update data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as {
      data: PetAPIResponseType;
    };

    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const removeUserPets = async (accessToken: string, petIDs: string[]) => {
  try {
    const response = await fetch(`${apiUrl}/user/pets`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ ids: petIDs }),
    });

    if (!response.ok) {
      logErrors(`Unable to fetch data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as { message: string };
    if (!data) {
      logErrors("No data in response");
      return;
    }

    return data;
  } catch (error) {
    logErrors(error);
  }
};

export const getPetsPaginated = async (searchParams: SearchParamsType) => {
  const data = {};
  if (searchParams.lastId) {
    data["last"] = searchParams.lastId;
  }
  if (searchParams.country) {
    data["country"] = searchParams.country;
  }
  if (searchParams.size) {
    data["size"] = searchParams.size;
  }
  if (searchParams.gender) {
    data["gender"] = searchParams.gender;
  }
  const params = new URLSearchParams(data);

  const response = await fetch(`${apiUrl}/pets?${params.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  }).catch((error: string) => {
    throw Error(error);
  });

  if (response.ok) {
    const data = (await response.json()) as PetPaginatedAPIResponseType;
    return data.data ?? Promise.reject(new Error(`No data`));
  } else {
    return Promise.reject(new Error(`Unable to fetch data`));
  }
};

export const getPetByID = async (id: string) => {
  const response = await fetch(`${apiUrl}/pets/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  }).catch((error: string) => {
    throw Error(error);
  });

  if (response.ok) {
    const data = await response.json();
    return data.data ?? Promise.reject(new Error(`No data`));
  } else {
    return Promise.reject(new Error(`Unable to fetch data`));
  }
};

export const getUserInfo = async (accessToken: string) => {
  const response = await fetch(`${apiUrl}/user`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  }).catch((error: string) => {
    throw Error(error);
  });

  if (response.ok) {
    const data = await response.json();
    return data.data ?? Promise.reject(new Error(`No data`));
  } else {
    return Promise.reject(new Error(`Unable to fetch data`));
  }
};

export const signIn = async (username: string, password: string) => {
  const response = await fetch(`${apiUrl}/signin`, {
    method: "POST",
    body: JSON.stringify({
      username: username,
      password: password,
    }),
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  }).catch((error: string) => {
    throw Error(error);
  });

  if (response.ok) {
    const data = await response.json();
    return data.data ?? Promise.reject(new Error(`No data`));
  }

  if (response.status === 401) {
    const data = await response.json();
    return data ?? Promise.reject(new Error(`No data`));
  }

  return Promise.reject(new Error(`Unable to fetch data`));
};

export const googleSignIn = async (resp: { credential: string }) => {
  try {
    const response = await fetch(`${apiUrl}/googlesignin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ credential: resp.credential }),
    });

    if (!response.ok) {
      logErrors(`Unable to update data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as {
      data: {};
    };

    if (!data || !data.data) {
      logErrors("No user in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const refreshToken = async () => {
  const response = await fetch(`${apiUrl}/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
  }).catch((error: string) => {
    throw Error(error);
  });

  if (response.ok) {
    const data = await response.json();
    return data.data ?? Promise.reject(new Error(`No data`));
  }

  if (response.status === 401) {
    const data = await response.json();
    return data ?? Promise.reject(new Error(`No data`));
  }

  return Promise.reject(new Error(`Unable to fetch data`));
};

export const getAdoptionForPet = async (accessToken: string, petId: string) => {
  try {
    const response = await fetch(`${apiUrl}/user/adoptions/pet/${petId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      logErrors(`Unable to fetch data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const data = (await response.json()) as { data: {} };
    if (!data || !data.data) {
      logErrors("No data in response");
      return;
    }

    return data.data;
  } catch (error) {
    logErrors(error);
  }
};

export const createAdoption = async (accessToken: string, petId: string, payload: unknown) => {
  try {
    const response = await fetch(`${apiUrl}/user/adoptions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        petId,
        data: payload,
      }),
    });

    if (!response.ok) {
      logErrors(`Unable to save data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const responseData = (await response.json()) as {
      data: {};
    };

    if (!responseData || !responseData.data) {
      logErrors("No data in response");
      return;
    }

    return responseData.data;
  } catch (error) {
    logErrors(error);
  }
};

export const updateAdoption = async (accessToken: string, adoptionId: string, payload: unknown) => {
  try {
    const response = await fetch(`${apiUrl}/user/adoptions/${adoptionId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        ...payload,
      }),
    });

    if (!response.ok) {
      logErrors(`Unable to save data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const responseData = (await response.json()) as {
      data: {};
    };

    if (!responseData || !responseData.data) {
      logErrors("No data in response");
      return;
    }

    return responseData.data;
  } catch (error) {
    logErrors(error);
  }
};

export const succeedAdoption = async (accessToken: string, adoptionId: string) => {
  try {
    const response = await fetch(`${apiUrl}/user/adoptions/${adoptionId}/succeeded`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      logErrors(`Unable to save data. ${response.status}: ${response.statusText}`, {
        status: response.status,
      });
      return;
    }

    const responseData = (await response.json()) as {
      data: {};
    };

    if (!responseData || !responseData.data) {
      logErrors("No data in response");
      return;
    }

    return responseData.data;
  } catch (error) {
    logErrors(error);
  }
};
